import { motion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="privacy-page">
      <SEOHead 
        title="Privacy Policy"
        description="Privacy Policy for OnPoint Authority Systems, Inc. - How we collect, use, and protect your information."
        url="https://onpointauthority.com/privacy"
      />
      <Navbar />

      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Legal
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Privacy<br />
              <span className="text-primary">Policy</span>
            </h1>
            <p className="text-sm text-muted-foreground mb-12">
              Last updated: December 2025
            </p>

            <div className="prose prose-invert max-w-none space-y-8">
              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  OnPoint Authority Systems, Inc. ("Company," "we," "us," or "our"), a Delaware corporation, 
                  is committed to protecting your privacy. This Privacy Policy explains how we collect, use, 
                  disclose, and safeguard your information when you visit our website or submit an application 
                  for authority review.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">2. Information We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We collect information you voluntarily provide when you:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Submit an Authority Review application</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Contact us via email</li>
                  <li>Engage with our content</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  This may include: name, email address, company name, annual revenue range, business type, 
                  and information about your business objectives.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">3. How We Use Your Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use collected information to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Process and evaluate Authority Review applications</li>
                  <li>Communicate decisions (Approved, Hold, or Declined)</li>
                  <li>Send newsletter content (if subscribed)</li>
                  <li>Improve our services and website</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">4. Information Sharing</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We do not sell, trade, or rent your personal information to third parties. We may share 
                  information with service providers who assist in operating our website and conducting 
                  our business, subject to confidentiality agreements.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">5. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction. However, 
                  no method of transmission over the Internet is 100% secure.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">6. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Depending on your jurisdiction, you may have the right to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">7. Cookies</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our website may use cookies and similar tracking technologies to enhance your experience. 
                  You can set your browser to refuse cookies, but some features may not function properly.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">8. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions about this Privacy Policy or our data practices, contact us at:
                </p>
                <div className="mt-4 p-6 border border-white/10 bg-card/5">
                  <p className="text-foreground font-medium">OnPoint Authority Systems, Inc.</p>
                  <p className="text-muted-foreground">A Delaware Corporation</p>
                  <p className="text-primary mt-2">contact@onpointauthority.com</p>
                </div>
              </section>

              <section>
                <h2 className="font-serif text-2xl text-foreground mb-4">9. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes 
                  by posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
